import { useCallback, useEffect, useState } from 'react';
import type { Dataset } from '../../shared/types';
import { useAuth } from '../auth/AuthProvider';

const DEV_DATA = import.meta.env.VITE_DEV_DATA === 'true';

interface DataState {
  data: Dataset | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Loads the dashboard dataset.
 * - Dev mode (VITE_DEV_DATA=true): imports the seed directly (no auth, no Function).
 * - Otherwise: fetches the auth-gated /api/data endpoint with the ID token.
 */
export function useData(): DataState {
  const { authedFetch, status } = useAuth();
  const [data, setData] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (status !== 'signedIn') return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        if (DEV_DATA) {
          const mod = await import('../../shared/dataset');
          if (!cancelled) setData(mod.dataset);
          return;
        }
        const res = await authedFetch('/api/data');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as Dataset;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Daten konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authedFetch, status, tick]);

  return { data, loading, error, reload };
}
